/* eslint-disable no-underscore-dangle */
import React, { useState } from 'react'
import { Card, Tabs, Tooltip } from 'antd'
import { useTranslation } from 'react-i18next'
import { useFinancialStatementsState, type FinacialStatementType } from '../../index'
import ErrorComponent from '../../../../components/Error/ErrorComponent'
import DocumentationLink from '../../../../components/Misc/DocumentationLink'
import PageHeaderWrapper from '../../../../components/PageHeaderWrapper/PageHeaderWrapper'
import IncomeStatementTable from '../StatementTable/IncomeStatementTable'
import BalanceSheetTable from '../StatementTable/BalanceSheetTable'
import { ModalProvider } from '../StatementTable/ModalContext'
import { DriverDataProvider } from '../../../driverData/context/DriverDataContext'
import { DeprecationPlanProvider } from '../../../deprecationPlan/context/DeprecationPlanContext'
import { DisplayScale } from '../../../../shared/utils/numberScale'

interface FinancialStatementsContentProps {
  isBudgetingPage?: boolean
}

const FinancialStatementsContent: React.FC<FinancialStatementsContentProps> = ({ isBudgetingPage = false }) => {
  const { t } = useTranslation()
  const {
    companyId,
    statementData,
    columns,
    statementLoading,
    statementFetching,
    statementError,
    statementRefetch,
    financialStatementsFilters,
    dimensionMap,
    dimensionList,
    getDimensionParams
  } = useFinancialStatementsState(isBudgetingPage)

  const isLoading = statementLoading || statementFetching
  const hasError = statementError
  const error = statementError

  // Shared across all statement tabs (income statement, assets, liabilities)
  // so the display scale/decimals choice stays consistent across the whole report.
  const [displayScale, setDisplayScale] = useState<DisplayScale>(DisplayScale.default)
  const [decimals, setDecimals] = useState<number>(0)

  if (!companyId) {
    return (
      <Card>
        <ErrorComponent error={{ message: t('errors:noCompanySelected') } as any} />
      </Card>
    )
  }

  // Tab configuration
  const tabList = [
    {
      key: 'incomeStatement' as FinacialStatementType,
      label: t('financialStatementsPage:incomeStatement'),
      children: (
        <Card>
          <div style={{ height: '85vh', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <IncomeStatementTable
              treeData={statementData?.incomeStatement ?? []}
              columns={columns}
              loading={isLoading}
              isBudgetingPage={isBudgetingPage}
              companyId={companyId}
              financialStatementsFilters={financialStatementsFilters}
              dimensionMap={dimensionMap}
              dimensionList={dimensionList}
              getDimensionParams={getDimensionParams}
              displayScale={displayScale}
              onDisplayScaleChange={setDisplayScale}
              decimals={decimals}
              onDecimalsChange={setDecimals}
            />
          </div>
        </Card>
      )
    },
    {
      key: 'assets' as FinacialStatementType,
      label: t('financialStatementsPage:assets'),
      children: (
        <Card>
          <div style={{ height: '85vh', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <BalanceSheetTable
              treeData={statementData?.assets ?? []}
              columns={columns}
              loading={isLoading}
              statementType="assets"
              companyId={companyId}
              financialStatementsFilters={financialStatementsFilters}
              displayScale={displayScale}
              onDisplayScaleChange={setDisplayScale}
              decimals={decimals}
              onDecimalsChange={setDecimals}
            />
          </div>
        </Card>
      )
    },
    {
      key: 'liabilities' as FinacialStatementType,
      label: t('financialStatementsPage:liabilities'),
      children: (
        <Card>
          <div style={{ height: '85vh', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <BalanceSheetTable
              treeData={statementData?.liabilities ?? []}
              columns={columns}
              loading={isLoading}
              statementType="liabilities"
              companyId={companyId}
              financialStatementsFilters={financialStatementsFilters}
              displayScale={displayScale}
              onDisplayScaleChange={setDisplayScale}
              decimals={decimals}
              onDecimalsChange={setDecimals}
            />
          </div>
        </Card>
      )
    }
  ]

  return (
    <PageHeaderWrapper
      title={t('menu:/budgeting/financial-statements')}
      subTitle={
        <Tooltip placement="right" title="info">
          <DocumentationLink route={{ path: '/budgeting' }} />
        </Tooltip>
      }
      ghost={false}
    >
      {hasError ? <ErrorComponent error={error as any} refetch={statementRefetch} /> : <Tabs items={tabList} />}
    </PageHeaderWrapper>
  )
}

interface FinancialStatementsContainerProps {
  isBudgetingPage?: boolean
}

const FinancialStatementsContainer: React.FC<FinancialStatementsContainerProps> = ({ isBudgetingPage = false }) => {
  return (
    <DriverDataProvider>
      <DeprecationPlanProvider>
        <ModalProvider>
          <FinancialStatementsContent isBudgetingPage={isBudgetingPage} />
        </ModalProvider>
      </DeprecationPlanProvider>
    </DriverDataProvider>
  )
}

export default FinancialStatementsContainer
